import type { PaletteCommand } from '@/components/command-palette/types'
import { useNavScope } from '@/hooks/useNavScope'
import { announce, stripGoToPrefix } from '@/lib/announcer'
import { orgPath, projectPath } from '@/lib/routeScope'
import type { SequenceStarter } from '@/lib/useSequence'
import { useIsAdmin } from '@/utils/permissions'
import { useRouter } from '@tanstack/react-router'
import { useCallback, useMemo } from 'react'

/**
 * The command id `G then <letter>` opens, for every letter whose destination is just "go to the page
 * the palette already has a command for" (gated by the exact same role, lab, module and plan rules,
 * since it is the same command). `P`, "current project's home", resolves to the same landing as `D`.
 * `U` and `A` are not here: Session Rules and Assets are routed pages with no sidebar or palette entry
 * of their own, so they navigate directly below instead of through a command lookup.
 */
export const NAV_COMMAND_ID_BY_KEY: Record<string, string> = {
	d: 'nav-project-device-management',
	p: 'nav-project-device-management',
	b: 'nav-project-dashboards',
	e: 'nav-project-exporters',
	r: 'nav-project-remote-commands',
	f: 'nav-project-sessions',
	c: 'nav-project-ota-campaigns',
	// No clean initial letter was free: D, B, P and C already name other destinations.
	h: 'nav-project-dbc-parsers',
	o: 'nav-org-org-inventory',
}

const GO_TO_CONTINUATIONS = new Set([...Object.keys(NAV_COMMAND_ID_BY_KEY), 's', 'u', 'a'])

/** `G then X`: go to a page. */
export function useGoToStarter(commands: readonly PaletteCommand[]): SequenceStarter {
	const router = useRouter()
	const { orgId, tenantId } = useNavScope()
	const isAdmin = useIsAdmin()

	// A single, always-somewhere settings shortcut, unlike `S then G`'s literal (and admin-gated)
	// General page: a non-admin here lands on Metadata instead of nothing.
	const goToSettings = useCallback(() => {
		if (!orgId) {
			return
		}
		const to = tenantId
			? projectPath(orgId, tenantId, isAdmin ? '/settings/general' : '/settings/metadata')
			: orgPath(orgId, '/settings/general')
		router.navigate({ to })
		announce('Settings')
	}, [router, orgId, tenantId, isAdmin])

	// Session Rules and Assets exist as real routes with no sidebar link, so they are gated on the
	// parent module they belong under (Sessions, Remote Commands) being available right now, the same
	// role/lab/plan rules the palette already applied when it built `commands`.
	const goToUnlisted = useCallback(
		(requiredCommandId: string, path: string, title: string) => {
			if (!orgId || !tenantId || !commands.some(c => c.id === requiredCommandId)) {
				return
			}
			router.navigate({ to: projectPath(orgId, tenantId, path) })
			announce(title)
		},
		[router, orgId, tenantId, commands],
	)

	const onComplete = useCallback(
		(key: string) => {
			if (key === 's') {
				goToSettings()
				return
			}
			if (key === 'u') {
				goToUnlisted('nav-project-sessions', '/sessions/rules/all', 'Session Rules')
				return
			}
			if (key === 'a') {
				goToUnlisted('nav-project-remote-commands', '/actions/assets/files', 'Assets')
				return
			}
			const id = NAV_COMMAND_ID_BY_KEY[key]
			const command = commands.find(c => c.id === id)
			if (!command) {
				return
			}
			command.run()
			announce(stripGoToPrefix(command.title))
		},
		[commands, goToSettings, goToUnlisted],
	)

	return useMemo(() => ({ key: 'g', continuations: GO_TO_CONTINUATIONS, onComplete }), [onComplete])
}
